import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  JournalEntry,
  JournalEntryLine,
  CreateJournalEntryInput,
  JournalType,
  EntryStatus,
  FiscalYear,
} from '@/types/accounting';

// =====================================================
// JOURNAL ENTRIES QUERIES
// =====================================================

// Fetch all journal entries
export function useJournalEntries(
  companyId: string,
  filters?: {
    dateFrom?: string;
    dateTo?: string;
    journalType?: JournalType;
    status?: EntryStatus;
    search?: string;
  }
) {
  return useQuery({
    queryKey: ['journal-entries', companyId, filters],
    queryFn: async () => {
      let query = supabase
        .from('journal_entries')
        .select(
          `
          *,
          lines:journal_entry_lines(*)
        `
        )
        .eq('company_id', companyId)
        .order('entry_date', { ascending: false })
        .order('entry_number', { ascending: false });

      if (filters?.dateFrom) {
        query = query.gte('entry_date', filters.dateFrom);
      }
      if (filters?.dateTo) {
        query = query.lte('entry_date', filters.dateTo);
      }
      if (filters?.journalType) {
        query = query.eq('journal_type', filters.journalType);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.search) {
        query = query.or(
          `entry_number.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
        );
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as (JournalEntry & { lines: JournalEntryLine[] })[];
    },
    enabled: !!companyId,
    staleTime: 30000, // 30 seconds
  });
}

// Fetch single journal entry
export function useJournalEntry(entryId: string) {
  return useQuery({
    queryKey: ['journal-entries', entryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('journal_entries')
        .select(
          `
          *,
          lines:journal_entry_lines(*)
        `
        )
        .eq('id', entryId)
        .single();

      if (error) throw error;
      return data as JournalEntry & { lines: JournalEntryLine[] };
    },
    enabled: !!entryId,
  });
}

// Fetch fiscal years
export function useFiscalYears(companyId: string) {
  return useQuery({
    queryKey: ['fiscal-years', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fiscal_years')
        .select('*')
        .eq('company_id', companyId)
        .order('year', { ascending: false });

      if (error) throw error;
      return data as FiscalYear[];
    },
    enabled: !!companyId,
    staleTime: 300000, // 5 minutes
  });
}

// Get current fiscal year
export function useCurrentFiscalYear(companyId: string) {
  return useQuery({
    queryKey: ['current-fiscal-year', companyId],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('fiscal_years')
        .select('*')
        .eq('company_id', companyId)
        .lte('start_date', today)
        .gte('end_date', today)
        .eq('status', 'open')
        .single();

      if (error) throw error;
      return data as FiscalYear;
    },
    enabled: !!companyId,
  });
}

// =====================================================
// JOURNAL ENTRIES MUTATIONS
// =====================================================

// Create journal entry
export function useCreateJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      entry,
      lines,
    }: {
      entry: Omit<CreateJournalEntryInput, 'lines'> & {
        company_id: string;
        created_by: string;
      };
      lines: CreateJournalEntryInput['lines'];
    }) => {
      // Validate balance
      const totalDebit = lines.reduce((sum, line) => sum + line.debit, 0);
      const totalCredit = lines.reduce((sum, line) => sum + line.credit, 0);

      if (Math.abs(totalDebit - totalCredit) > 0.01) {
        throw new Error(
          `Entry not balanced: Debit ${totalDebit.toFixed(2)} != Credit ${totalCredit.toFixed(2)}`
        );
      }

      // Create entry
      const { data: createdEntry, error: entryError } = await supabase
        .from('journal_entries')
        .insert([
          {
            ...entry,
            total_debit: totalDebit,
            total_credit: totalCredit,
            is_balanced: true,
          },
        ])
        .select()
        .single();

      if (entryError) throw entryError;

      // Create lines
      const linesWithEntryId = lines.map((line, index) => ({
        ...line,
        entry_id: createdEntry.id,
        line_number: index + 1,
      }));

      const { error: linesError } = await supabase
        .from('journal_entry_lines')
        .insert(linesWithEntryId);

      if (linesError) throw linesError;

      return createdEntry as JournalEntry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
    },
  });
}

// Post journal entry
export function usePostJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ entryId, userId }: { entryId: string; userId: string }) => {
      const { data, error } = await supabase
        .from('journal_entries')
        .update({
          status: 'posted',
          posted_at: new Date().toISOString(),
          posted_by: userId,
        })
        .eq('id', entryId)
        .eq('status', 'draft') // Only post drafts
        .select()
        .single();

      if (error) throw error;
      return data as JournalEntry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
    },
  });
}

// Cancel journal entry
export function useCancelJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      const { data, error } = await supabase
        .from('journal_entries')
        .update({
          status: 'cancelled',
        })
        .eq('id', entryId)
        .select()
        .single();

      if (error) throw error;
      return data as JournalEntry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
    },
  });
}
